/**
 * Keeps a url out of Datadog. Logger context reaches Datadog before the pino
 * redact list is applied (ARCFE-679), so anything a call site puts in there is
 * already outside the redaction it might be relying on.
 *
 * A path can name a patient or a document as readily as a query can, so the
 * most this returns is the origin — or the bare scheme when the OS rather than
 * the web owns the url, since a `mailto:` carries a recipient and a body and a
 * `tel:` is the identifier itself.
 */
export function describeUrlForLog(url: string): string {
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
      ? parsed.origin
      : parsed.protocol;
  } catch {
    return 'unparseable';
  }
}

/**
 * The same, for an error whose message embeds a url. The opener plugin's scope
 * rejection is `Not allowed to open url {url}` — it interpolates the whole
 * thing — so redacting a `url` field and logging the message beside it puts
 * back exactly what was just removed.
 *
 * Matching runs to the next whitespace, which is where a url ends in every
 * message this has to handle: the schemes below all percent-encode spaces, and
 * opener stringifies a `URL` before interpolating. A hand-built message
 * carrying a *decoded* url would leave its tail behind.
 */
export function describeErrorForLog(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  return message.replace(/\b[a-z][a-z0-9+.-]*:(?:\/\/)?\S+/gi, (url) =>
    describeUrlForLog(url)
  );
}
